import axios from 'axios'

export type UploadStatus = 'idle' | 'uploading' | 'processing' | 'complete' | 'error'

export interface UploadResult {
  fileId: string
  uploadId: string
  message: string
}

export interface UploadOptions {
  file: File
  workflow: 'router' | 'standard' | 'advanced'
  onProgress?: (progress: number) => void
}

/**
 * Map frontend workflow names to backend workflow IDs.
 */
const WORKFLOW_MAP: Record<UploadOptions['workflow'], string> = {
  router: 'phenotype-aware-extraction',
  standard: 'gemini-optimized-ingestion',
  advanced: 'deterministic-extraction',
}

/**
 * Generate unique upload ID.
 * Frontend generates this for immediate tracking.
 */
function generateUploadId(): string {
  return `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Upload a file for processing.
 *
 * Uses axios for upload progress tracking (fetch doesn't support this).
 * Maps to POST /api/v1/upload endpoint.
 *
 * @param options - File, workflow, and progress callback
 * @returns Upload result with fileId for navigation
 */
export async function uploadFile(options: UploadOptions): Promise<UploadResult> {
  const { file, workflow, onProgress } = options

  const uploadId = generateUploadId()
  const backendWorkflow = WORKFLOW_MAP[workflow]
  const formData = new FormData()
  formData.append('file', file)
  formData.append('workflowName', backendWorkflow)
  formData.append('uploadID', uploadId)
  formData.append('organizationID', 'default-org')

  const response = await axios.post<UploadResult>('/api/v1/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        onProgress(percentCompleted)
      }
    },
    withCredentials: true, // CRITICAL: Include session cookies
  })

  return {
    fileId: response.data.fileId || uploadId,
    uploadId,
    message: response.data.message || 'Upload successful',
  }
}
